import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { forwardRef, memo, useRef } from "react";
import { ButtonBase } from "./ButtonBase";
import { ClickBase } from "./ClickBase";
import { Circle, DoneOutline } from "../icons/icons";
import { ColorsProvider } from "../providers/ColorsProvider";
import { SlotsProvider } from "../providers/SlotsProvider";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { mergeSx, useUncontrolledAndControlledWithEvent, } from "../system";
export const radioClasses = {
    root: "MinervaRadio-root",
    checked: "MinervaRadio-checked",
    disabled: "MinervaRadio-disabled",
    "check-icon": "MinervaRadio-checkIcon",
    "radio-icon": "MinervaRadio-radioIcon",
};
export const radioSlots = {
    checkedIcon: DoneOutline,
    radioIcon: Circle,
};
export const radioVariants = ["radio", "check"];
export const Radio = memo(forwardRef((props, ref) => {
    const { variant = "radio", defaultChecked = false, checked, onChange, color = "surface", colorSet = "container", invertColors, size = "sm", slots, slotProps, disabled, className, sx, onClick, ...rest } = useDefaultProps({ props, component: "Radio" });
    const { internalValue: internalChecked, handleValueChange: handleChange } = useUncontrolledAndControlledWithEvent({
        defaultValue: defaultChecked,
        value: checked,
        onValueChange: onChange,
    });
    const inputRef = useRef(null);
    const widthHeight = {
        xxs: 4,
        xs: 4.5,
        sm: 5,
        md: 6,
        lg: 7,
        xl: 8,
    };
    return (_jsx(SlotsProvider, { baseSlots: radioSlots, slots: slots, children: ({ checkedIcon: CheckedIcon, radioIcon: RadioIcon }) => (_jsx(ColorsProvider, { color: disabled ? "disabled" : color, colorSet: colorSet, invertColors: disabled ? false : variant === "check" && checked ? true : invertColors, children: ({ color, background }) => (_jsxs(ButtonBase, { ref: ref, disabled: disabled, className: clsx([
                    radioClasses.root,
                    internalChecked && radioClasses.checked,
                    disabled && radioClasses.disabled,
                    className,
                ]), onClick: (e) => {
                    inputRef.current?.click();
                    onClick?.(e);
                }, sx: mergeSx({
                    color,
                    background,
                    display: "flex",
                    position: "relative",
                    margin: 0,
                    width: widthHeight[size],
                    height: widthHeight[size],
                    // Safari stretching fix
                    fontSize: 0,
                    outline: "none",
                    borderRadius: (theme) => theme.radius.full,
                    borderColor: (theme) => theme.palette.outline,
                    border: 1,
                    cursor: "pointer",
                    "&:hover": {
                        borderColor: color,
                    },
                    [`&.${radioClasses.checked}`]: {
                        borderColor: color,
                    },
                    [`&.${radioClasses.disabled}`]: {
                        color,
                        background,
                    },
                }, sx), ...rest, children: [_jsx(ClickBase, { ref: inputRef, as: "input", type: "radio", checked: internalChecked, disabled: disabled, onChange: (e) => handleChange(e, !internalChecked), sx: {
                            opacity: 0,
                            width: "100%",
                            height: "100%",
                        } }), internalChecked &&
                        (variant === "radio" ? (_jsx(RadioIcon, { ...slotProps?.checkedIcon, className: clsx([
                                radioClasses["radio-icon"],
                                slotProps?.radioIcon?.className,
                            ]), sx: mergeSx({
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: widthHeight[size] - 1,
                                height: widthHeight[size] - 1,
                            }, slotProps?.checkedIcon?.sx) })) : (_jsx(CheckedIcon, { ...slotProps?.checkedIcon, className: clsx([
                                radioClasses["check-icon"],
                                slotProps?.checkedIcon?.className,
                            ]), sx: mergeSx({
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                            }, slotProps?.checkedIcon?.sx) })))] })) })) }));
}));
//# sourceMappingURL=Radio.js.map